import { Locale } from "@/Common/Models/Localization/Locale";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { Listener } from "@/Common/Stores/Listener";
import { OverlayOptions } from "@/Common/Stores/OverlayStore";

/**
 * Type for mutateState function if needed when passed as prop or parameter.
 */
type MutateState<TState> = (mutation: (state: TState) => void, then?: () => void) => void;

/**
 * The props argument type for a React component that has no props of its own.
 */
type EmptyProps = Record<never, never>; // we can't use `never` because React unions it with its built-in props, and `never & anything` is always `never`.

/**
 * Wraps the React component class with methods to manage the component lifecycle.
 *
 * NOTE: subclasses must call super.componentWillUnmount if they override it.
 */
class BaseReactComponent<TProps = EmptyProps, TState = never> extends React.Component<TProps, TState> {
    private listener: Listener;
    private debounceTimeOut: number = -1;

    /**
     * The unique key for the component's overlay, if any.
     */
    private overlayKey: string;

    /**
     * The locales for which `getString` can get translations, or empty to allow any locale.
     */
    private acceptableLocales: Locale[] = [];

    /**
     * Clean up the component state when it's unmounting.
     */
    public componentWillUnmount(): void {
        if (this.listener)
            this.listener.unregister();

        if (this.overlayKey)
            overlayStore.hideOverlay(this.overlayKey);
    }

    /**
     * Add an operation handler.
     * @param operation The operation to handle.
     * @param handle The callback to invoke.
     */
    public addHandle(operation: number, handle: (request: LocalEvent) => void): void {
        if (!this.listener)
            this.listener = new Listener(LocalEventDispatcher);

        this.listener.AddHandle(operation, handle);
    }

    /**
     * Add a component overlay that will be removed automatically when the component unmounts.
     * @param key The unique key for the component's overlay.
     * @param options The optional overlay settings.
     */
    public setOverlay(key: string, options: OverlayOptions = null): void {
        if (this.overlayKey)
            overlayStore.hideOverlay(this.overlayKey);

        if (key)
            overlayStore.showOverlay(key, options);

        this.overlayKey = key;
    }

    /**
     * Change the component state.
     * @param mutation Apply changes to the state.
     * @param then The callback to invoke when the update is applied, if any.
     */
    public mutateState(mutation: (state: TState) => void, then: () => void = null): void {
        this.setState(
            s => {
                const newState = { ...s as TState };
                mutation(newState);
                return newState;
            },
            then
        );
    }

    public debouncedHandle(callback: () => void): void {
        clearTimeout(this.debounceTimeOut);
        this.debounceTimeOut = window.setTimeout(() => callback(), 250);
    }

    /**
     * Get a translation for the current locale if it matches @property acceptableLocales, else from the first locale listed in that property.
     * @param key The translation key to fetch.
     * @param tokens An object containing token values to substitute in the string.
     */
    protected getString(key: string, tokens: object = {}): string {
        if (!this.acceptableLocales.any())
            return cacheStore.GetString(key, tokens);

        const locale = this.acceptableLocales.contains(sessionStore.locale)
            ? sessionStore.locale
            : this.acceptableLocales.firstOrDefault();

        return cacheStore.GetString(
            key,
            tokens,
            locale
        );
    }

    /**
     * Set the locales for which `getString` can get translations. This will asynchronously load translations for the first locale if it's not loaded yet.
     * Note - The default locale resources should be loaded in the parent component by calling the cacheStore.RequestApplicationStringAsync(Locale) function.
     * @param locales The list of allowed locale codes, or an empty list to allow any locale.
     * @param loadPreferredLocaleStrings If the application strings should be loaded when acceptable locales are set.
     */
    protected setAcceptableLocales(locales: Locale[]): void {
        this.acceptableLocales = locales ?? [];

        let preferred = this.acceptableLocales.firstOrDefault();

        if (preferred)
            cacheStore.RequestApplicationStringAsync(preferred);
    }
}

export { BaseReactComponent, EmptyProps, MutateState };
